import { Injectable } from '@angular/core';
import { SparepartSearchHistoryResult, SparepartSearchOptions } from '@vi-succ/sparepart-domain';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class EtSparepartSearchHistoryPersistenceService {
  private static STORAGE_KEY = 'SPAREPARTS_SEARCH_HISTORY';
  private static STORAGE_LAST_ID_KEY = 'SPAREPARTS_SEARCH_HISTORY_LAST_ID';
  private static MAX_HISTORY_SIZE = 20;

  private history: SparepartSearchHistoryResult[] = [];
  private lastId = 0;

  constructor(private storageService: StorageService) {
    Promise.all([
      this.storageService.get<string>(EtSparepartSearchHistoryPersistenceService.STORAGE_KEY),
      this.storageService.get<string>(EtSparepartSearchHistoryPersistenceService.STORAGE_LAST_ID_KEY),
    ]).then(([storedHistory, storedLastId]) => {
      this.history = JSON.parse(storedHistory || '[]');
      this.lastId = Number(storedLastId ?? '0');
      this.normalizeIds();
    });
  }

  saveEntry(entry: SparepartSearchHistoryResult): void {
    // Avoid consecutive duplicates for the same term
    if (this.history.length > 0) {
      const last = this.history[this.history.length - 1];
      if (last.term === entry.term) return;
    }

    this.history.push(entry);
    this.reduceHistoryToLimit();
    this.saveToStorage();
  }

  private reduceHistoryToLimit() {
    if (this.history.length > EtSparepartSearchHistoryPersistenceService.MAX_HISTORY_SIZE) {
      this.history = this.history.slice(-EtSparepartSearchHistoryPersistenceService.MAX_HISTORY_SIZE);
    }
  }

  deleteEntryById(id: number): void {
    this.history = this.history.filter((entry) => entry.id !== id);
    this.saveToStorage();
  }

  deleteAllEntries(): void {
    this.history = [];
    this.lastId = 0;
    this.saveToStorage();
  }

  getPagedResults(options: SparepartSearchOptions): SparepartSearchHistoryResult[] {
    const history = this.history;
    const page = options.pageIndex ?? 0;
    const pageSize = options.pageSize ?? 10;

    const sortField = (options.sortField ?? 'createdDate') as keyof SparepartSearchHistoryResult;
    const sortDirection = options.sortDirection ?? 'desc';

    const results = [...history];

    // Sort paged results by sort field and direction
    results.sort((a, b) => {
      let compareResult = 0;

      if (typeof a[sortField] === 'string' && typeof b[sortField] === 'string') {
        compareResult = a[sortField].localeCompare(b[sortField]);
      }
      if (typeof a[sortField] === 'number' && typeof b[sortField] === 'number') {
        compareResult = a[sortField] - b[sortField];
      }
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });

    return results.slice(page * pageSize, page * pageSize + pageSize);
  }

  getSize() {
    return this.history.length;
  }

  getNextId(): number {
    this.lastId += 1;
    this.storageService.set(EtSparepartSearchHistoryPersistenceService.STORAGE_LAST_ID_KEY, String(this.lastId));
    return this.lastId;
  }

  // Migration: normalize pre-existing data in localStorage.
  // Ensures IDs are unique and aligns lastId with the maximum existing ID.
  private normalizeIds(): void {
    const currentMaxId = Math.max(0, ...this.history.map((entry) => entry.id));
    if (this.lastId < currentMaxId) this.lastId = currentMaxId;

    const seen = new Set<number>();

    for (const entry of this.history) {
      if (seen.has(entry.id)) entry.id = this.getNextId();
      seen.add(entry.id);
    }

    this.reduceHistoryToLimit();
    this.saveToStorage();
  }

  private saveToStorage(): void {
    this.storageService.set(EtSparepartSearchHistoryPersistenceService.STORAGE_KEY, JSON.stringify(this.history));
    this.storageService.set(EtSparepartSearchHistoryPersistenceService.STORAGE_LAST_ID_KEY, String(this.lastId));
  }
}
